import type { Official } from './apiClient';

export interface FeaturedOfficialAction {
  official: Official;
  shareTitle: string;
  shareText: string;
  shareUrlPath: string;
  whyItMatters: string;
  adoptionExplanation: string;
}

function normalizeText(value: string | null | undefined): string {
  return (value ?? '').trim();
}

function hasMeaningfulSummary(official: Official): boolean {
  return normalizeText(official.accountability_summary).length > 0;
}

function hasHeadshot(official: Official): boolean {
  return normalizeText(official.photo_url).length > 0;
}

function lifecyclePriority(official: Official): number {
  switch (official.lifecycle_status) {
    case 'vying':
      return 3;
    case 'active':
    case undefined:
      return 2;
    case 'inactive':
      return 1;
    case 'archived':
      return 0;
    default:
      return 1;
  }
}

function scorePriority(official: Official): number {
  return official.accountability_score ?? -1;
}

function adoptionPriority(official: Official): number {
  return official.adoption_count ?? 0;
}

export function chooseFeaturedOfficial(officials: Official[]): Official | null {
  if (officials.length === 0) return null;

  const ranked = [...officials].sort((a, b) => {
    const meaningfulDiff = Number(hasMeaningfulSummary(b)) - Number(hasMeaningfulSummary(a));
    if (meaningfulDiff !== 0) return meaningfulDiff;

    const lifecycleDiff = lifecyclePriority(b) - lifecyclePriority(a);
    if (lifecycleDiff !== 0) return lifecycleDiff;

    const scoreDiff = scorePriority(b) - scorePriority(a);
    if (scoreDiff !== 0) return scoreDiff;

    const adoptionDiff = adoptionPriority(b) - adoptionPriority(a);
    if (adoptionDiff !== 0) return adoptionDiff;

    const photoDiff = Number(hasHeadshot(b)) - Number(hasHeadshot(a));
    if (photoDiff !== 0) return photoDiff;

    return a.name.localeCompare(b.name);
  });

  return ranked[0] ?? null;
}

export function buildWhyItMatters(official: Official): string {
  const summary = normalizeText(official.accountability_summary);
  if (summary) return summary;

  const office = normalizeText(official.office_name) || 'public office';
  const state = normalizeText(official.state);
  const district = normalizeText(official.district);
  const place = district ? `${state ? `${state} ` : ''}district ${district}` : state || 'their community';
  return `${official.name} holds ${office} and directly affects ${place}. This profile helps citizens track the record, the rhetoric, and the receipts in one place.`;
}

export function buildFeaturedOfficialAction(official: Official): FeaturedOfficialAction {
  const shareUrlPath = `/officials/${encodeURIComponent(official.id)}`;
  const office = normalizeText(official.office_name) || 'public official';
  const state = normalizeText(official.state);
  const stateSegment = state ? ` (${state})` : '';
  const shareTitle = `Adopt-A-Coward: ${official.name}${stateSegment}`;
  const whyItMatters = buildWhyItMatters(official);
  const scoreSegment = official.accountability_score != null ? ` Score: ${official.accountability_score}/100.` : '';
  const shareText = `${official.name} is tracked on Adopt-A-Coward as a ${office}.${scoreSegment} Read the dossier, adopt the profile, and help keep public power under a microscope.`;

  return {
    official,
    shareTitle,
    shareText,
    shareUrlPath,
    whyItMatters,
    adoptionExplanation: 'Adopting means you follow this profile, boost its visibility, and get an easier path back to the dossier and updates.',
  };
}
